import React, { useEffect, useContext, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { fetchCredentialingDevices, postEnrollAxiadid, postUpdateAxiadid } from '../../utils/credentialing';
import qrScanImg from '../../images/qr-scan.svg';
import ProgressTemplate from '../shared/ProgressTemplate';
import { AppDispatchContext, AppStateContext } from '../../context/AppContext';
import { EnrollingDispatchContext, EnrollingStateContext } from '../../context/EnrollingContext';
import { useWorkflow } from '../../context/WorkflowContext';
import CardEnrollLayout from '../shared/CardEnrollLayout';
import CurrentPin from '../formLibrary/CurrentPin';
import {
    PROGRESS_BAR_DONE_DELAY,
    enrollAxiadidGuidanceId,
    enrollAxiadidSupportLinkId,
    apiPath,
    paths,
} from '../../constants';


// Maps sponsor device type to the i18n key for the success message.
// No sponsor → "Mobile PKI credential issuance successful"
// New sponsor types can be added here with their own message key.
const completedMessageKeys = {
    piv: 'enrollAxiadid$completedDerived',
};
const defaultCompletedMessageKey = 'enrollAxiadid$completedMobile';

const humanizeStage = (stage) => {
    if (!stage) return null;
    // Use the part after the colon if present (e.g. "axiadid:create" → "create")
    const s = (stage.includes(':') ? stage.split(':').pop() : stage).replace(/_/g, ' ').trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const useQRCountdown = (qrData, qrTimeoutSeconds) => {
    const [remaining, setRemaining] = useState(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!qrData || qrTimeoutSeconds == null) {
            setRemaining(null);
            return;
        }

        const expiresAt = qrData.timestamp + qrTimeoutSeconds * 1000;

        const tick = () => {
            const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
            setRemaining(left);
            if (left <= 0 && intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        tick();
        intervalRef.current = setInterval(tick, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [qrData, qrTimeoutSeconds]);

    return remaining;
};

const EnrollAxiadid = ({ mgmt }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const returnPath = pathname?.includes(paths.helpDesk) ? paths.helpDesk : paths.identities;
    const dispatch = useContext(AppDispatchContext);
    const enrollingDispatch = useContext(EnrollingDispatchContext);
    const { webpcscMode, device } = useContext(AppStateContext);
    const { username, reader, sponsorDeviceType } = useContext(EnrollingStateContext);
    const { qrData, clearQR, qrTimeoutSeconds, qrConsumed, issuanceCompleted, issuanceError, pinFormData, clearPinForm } = useWorkflow();
    const [showNext, setShowNext] = useState(false);
    const [stage, setStage] = useState(t('pivActionInitialState'));
    const [issuanceStatus, setIssuanceStatus] = useState(null);
    const isUpdate = !!mgmt?.device;
    const [guidanceDismissed, setGuidanceDismissed] = useState(!isUpdate);
    const updateStartedRef = useRef(false);

    // Local PIN form state
    const [currentPinValue, setCurrentPinValue] = useState('');
    const [formValidators, setFormValidators] = useState({});

    const remaining = useQRCountdown(qrData, qrTimeoutSeconds);
    const qrExpired = remaining !== null && remaining <= 0 && !qrConsumed;

    const registerForm = (validators) => {
        setFormValidators(validators);
    };

    const handlePinSubmit = () => {
        if (formValidators.currentPin && formValidators.currentPin(currentPinValue) === true) {
            // Call the callback provided by the server script
            if (pinFormData?.callback) {
                pinFormData.callback(currentPinValue);
            }
        }
    };

    const handlePinCancel = () => {
        if (pinFormData?.cancelCallback) {
            pinFormData.cancelCallback();
        }
        clearPinForm();
    };

    const setProgress = (hook, data) => {
        if (hook === 'stage') {
            setStage(data);
        }
    };

    // Reset PIN input when error changes
    useEffect(() => {
        if (pinFormData?.error) {
            setCurrentPinValue('');
        }
    }, [pinFormData?.error]);

    // Show preparation guidance modal before starting update.
    // Mirror the new-issuance path: fetch credentialing devices to derive
    // sponsorDeviceType + availablePlatforms so the modal renders the
    // sponsor device card (e.g. PIV) and the iOS/Android badges, and the
    // post-issuance success message picks the right i18n key.
    useEffect(() => {
        if (!isUpdate || !username) return undefined;
        let cancelled = false;
        fetchCredentialingDevices(
            (json) => {
                if (cancelled) return;
                const keys = Object.keys(json || {});
                const lowerKeys = keys.map((k) => k.toLowerCase());
                const availablePlatforms = {
                    ios: lowerKeys.some((k) => k.includes('axiadid_ios')),
                    android: lowerKeys.some((k) => k.includes('axiadid_android')),
                };
                const axiadidKey = keys.find((k) => k.toLowerCase().includes('axiadid'));
                const derivedSponsorType = axiadidKey ? (json[axiadidKey].sponsorDeviceType || null) : null;
                enrollingDispatch({
                    type: 'SET_SPONSOR_DEVICE_TYPE',
                    sponsorDeviceType: derivedSponsorType,
                });
                dispatch({
                    type: 'SHOW_MODAL',
                    device: mgmt.device,
                    modal: {
                        type: 'PIV_SETUP_GUIDANCE',
                        closeAction: () => setGuidanceDismissed(true),
                        sponsorDeviceType: derivedSponsorType,
                        availablePlatforms,
                    },
                });
            },
            dispatch,
            username,
        );
        return () => { cancelled = true; };
    }, [isUpdate, username, dispatch, enrollingDispatch]);

    useEffect(() => {
        const setAxiadidResult = async (json) => {
            setShowNext(true);
            if (mgmt && json.step === 'done') {
                setTimeout(mgmt.next, PROGRESS_BAR_DONE_DELAY);
            }
        };

        if (mgmt?.device && guidanceDismissed && !updateStartedRef.current) {
            updateStartedRef.current = true;
            postUpdateAxiadid(
                setAxiadidResult,
                setProgress,
                dispatch,
                mgmt.device,
            );
        } else if (reader?.groups?.[0] && username) {
            const deviceData = {
                group: reader.groups[0],
                type: "Permanent",
                device: {
                    type: "axiadid"
                },
                persoDataSet: []
            };
            postEnrollAxiadid(
                setAxiadidResult,
                setProgress,
                dispatch,
                deviceData,
                username,
            );
        }
    }, [reader?.groups, username, webpcscMode, dispatch, mgmt, guidanceDismissed]);

    // Poll Portal backend to track QR session lifecycle:
    // 1. Waiting for scan → consumed (mobile scanned QR)
    // 2. Consumed → completed (issuance done) or error (issuance failed)
    // Stops polling once a terminal state (completed/error/expired) is reached,
    // or when the session timeout elapses (no more APDU exchanges are possible).
    useEffect(() => {
        if (!qrData?.session || issuanceCompleted || issuanceError || qrExpired) return;

        const controller = new AbortController();
        const currentSession = qrData.session;
        const sessionDeadline = qrTimeoutSeconds != null
            ? qrData.timestamp + qrTimeoutSeconds * 1000
            : null;

        const pollInterval = setInterval(async () => {
            if (controller.signal.aborted) return;
            if (sessionDeadline && Date.now() > sessionDeadline) {
                window.proxy?.issuanceError(t('enrollAxiadid$sessionTimeout'), 'timeout');
                clearInterval(pollInterval);
                return;
            }
            try {
                const res = await fetch(`${apiPath}/apdu/status/${currentSession}`, {
                    credentials: 'same-origin',
                    signal: controller.signal,
                });
                if (controller.signal.aborted) return;
                if (res.ok) {
                    const data = await res.json();
                    if (data.error) {
                        window.proxy?.qrConsumed();
                        window.proxy?.issuanceError(data.errorMessage || t('enrollAxiadid$issuanceError'));
                        clearInterval(pollInterval);
                    } else if (data.completed) {
                        window.proxy?.qrConsumed();
                        window.proxy?.issuanceCompleted();
                        clearInterval(pollInterval);
                    } else if (data.consumed) {
                        if (!qrConsumed) {
                            window.proxy?.qrConsumed();
                        }
                        if (data.statusMessage) {
                            setIssuanceStatus(data.statusMessage);
                        }
                    }
                }
            } catch (e) {
                if (e.name === 'AbortError') return;
                // Silently ignore other polling errors
            }
        }, 3000);

        return () => {
            controller.abort();
            clearInterval(pollInterval);
        };
    }, [qrData?.session, qrConsumed, issuanceCompleted, issuanceError, qrExpired, qrTimeoutSeconds, t]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearQR();
            clearPinForm();
        };
    }, [clearQR, clearPinForm]);

    const supportLink = (
        <div style={{ marginTop: '12px', fontSize: 14, lineHeight: '20px' }}>
            <span style={{ color: 'rgba(0, 15, 27, 0.60)' }}>{t('pivGuidanceModal$helpText')} </span>
            <a
                href={t('pivGuidanceModal$supportUrl')}
                target="_blank"
                rel="noopener noreferrer"
                data-test-id={enrollAxiadidSupportLinkId}
                style={{ color: '#0056E7' }}
            >
                {t('pivGuidanceModal$docLink')}
            </a>
        </div>
    );

    const formatCountdown = () => {
        if (remaining == null) return null;
        const mins = String(Math.floor(remaining / 60)).padStart(2, '0');
        const secs = String(remaining % 60).padStart(2, '0');
        return t('enrollAxiadid$expiresIn', { minutes: mins, seconds: secs });
    };

    const renderQRSection = () => {
        // Issuance completed — show success
        if (issuanceCompleted) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48, width: '100%', background: 'white', borderRadius: 16 }}>
                    <div style={{ textAlign: 'center', maxWidth: 480 }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">&#10003;</div>
                        <p style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: '24px', color: '#171818' }}>
                            {t(completedMessageKeys[sponsorDeviceType] || defaultCompletedMessageKey)}
                        </p>
                    </div>
                </div>
            );
        }

        // Issuance error — show error message
        if (issuanceError) {
            const isTimeout = issuanceError.type === 'timeout';
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48, width: '100%', background: 'white', borderRadius: 16 }}>
                    <div style={{ textAlign: 'center', maxWidth: 480 }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, lineHeight: '20px', color: '#c00000' }}>
                            {isTimeout ? t('enrollAxiadid$qrExpiredError') : t('enrollAxiadid$issuanceError')}
                        </p>
                        <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: '18px', color: 'rgba(0, 15, 27, 0.60)' }}>
                            {issuanceError.message}
                        </p>
                        {supportLink}
                    </div>
                </div>
            );
        }

        // QR consumed, issuance in progress — show live status from backend
        if (qrConsumed) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48, width: '100%', background: 'white', borderRadius: 16 }}>
                    <div style={{ textAlign: 'center', maxWidth: 480 }}>
                        <p style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: '24px', color: '#171818' }}>
                            {t('enrollAxiadid$consumed')}
                        </p>
                        <p style={{ margin: '12px 0 0', fontSize: 13, lineHeight: '18px', color: 'rgba(0, 15, 27, 0.60)' }} aria-live="polite">
                            {issuanceStatus ? `${t('enrollAxiadid$issuanceStagePrefix')} ${humanizeStage(issuanceStatus)}` : t('enrollAxiadid$issuanceInProgress')}
                        </p>
                    </div>
                </div>
            );
        }

        // QR expired — show restart message
        if (qrExpired) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48, width: '100%', background: 'white', borderRadius: 16 }}>
                    <div style={{ textAlign: 'center', maxWidth: 480 }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, lineHeight: '20px', color: '#c00000' }}>
                            {t('enrollAxiadid$expired')}
                        </p>
                        {supportLink}
                    </div>
                </div>
            );
        }

        // Active QR code
        return (
            <div style={{
                display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
                gap: '48px 128px', padding: 24, width: '100%', background: 'white', borderRadius: 16
            }}>
                {/* Left: illustration + instructions */}
                <div
                    data-test-id={enrollAxiadidGuidanceId}
                    style={{ flex: '0 1 520px', maxWidth: 520, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 24 }}
                >
                    <img
                        src={qrScanImg}
                        alt={t('enrollAxiadid$scanInstruction')}
                        style={{ width: '100%', maxWidth: 520, height: 160, objectFit: 'contain' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, lineHeight: '20px', color: '#171818' }}>
                            {t('enrollAxiadid$scanInstruction')}
                        </p>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 400, lineHeight: '20px', color: '#171818' }}>
                            {t('enrollAxiadid$scanSubtitle')}
                        </p>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, textDecoration: 'underline', lineHeight: '18px', color: 'rgba(0, 15, 27, 0.60)' }}>
                        {t('enrollAxiadid$securityReminder')}
                    </p>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: '18px', color: 'rgba(0, 15, 27, 0.60)' }}>
                        {t('enrollAxiadid$troubleshoot')}
                    </p>
                    {supportLink}
                </div>
                {/* Right: QR code + countdown */}
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <QRCode
                        size={320}
                        value={qrData.url}
                        viewBox="0 0 256 256"
                    />
                    {remaining != null && (
                        <p style={{ margin: '16px 0 0', fontSize: 13, fontWeight: 600, lineHeight: '18px', color: remaining <= 30 ? '#c00000' : 'rgba(0, 15, 27, 0.60)' }} aria-live={remaining <= 30 ? 'polite' : 'off'}>
                            {formatCountdown()}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <CardEnrollLayout
            cardTitle={t('cardTitles$axiadId')}
            title={pinFormData?.show ? (pinFormData.title || t('pinVerification')) : t('issuanceProgress')}
            alert={(issuanceCompleted || issuanceError || qrExpired) ? '' : t('enrollTemplateAlertDefault')}
            showNext={pinFormData?.show ? !!currentPinValue : qrData ? (issuanceCompleted || issuanceError || qrExpired) : showNext}
            mgmt={
                pinFormData?.show
                    ? {
                        next: handlePinSubmit,
                        back: handlePinCancel,
                        showBack: true,
                    }
                    : qrData
                    ? {
                        next: () => navigate(returnPath),
                        back: () => {},
                        showBack: false,
                    }
                  : mgmt
            }
            showBack={pinFormData?.show || (!pinFormData?.show && !qrData)}
            flowButtonProps={
                pinFormData?.show
                    ? { nextButtonText: t('modalLabels$submit'), backButtonText: t('enrollAxiadid$cancel'), showBackButtonIcon: false }
                    : qrData
                    ? { displayBack: false, nextButtonText: t('enrollAxiadid$done'), nextButtonLeadingIcon: 'check' }
                    : {}
            }
        >
            {/* Display PIN form when server requests it */}
            {pinFormData?.show ? (
                <div style={{ padding: '20px' }}>
                    {pinFormData.sponsorData && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <div>{t('sponsorReplacement$willReplace')}</div>
                                <strong>{t('sponsorReplacement$currentDevice')}</strong>
                                <div>{t('sponsorReplacement$serial')}: {pinFormData.sponsorData.currentSerial}</div>
                                <div>{t('sponsorReplacement$subjectDN')}: {pinFormData.sponsorData.currentSubjectDN}</div>
                            </div>
                            <div>
                                <strong>{t('sponsorReplacement$newDevice')}</strong>
                                <div>{t('sponsorReplacement$serial')}: {pinFormData.sponsorData.newSerial}</div>
                            </div>
                        </div>
                    )}
                    <CurrentPin
                        registerForm={registerForm}
                        setCurrentPin={setCurrentPinValue}
                        serverComponent={{
                            details: {
                                reset: false,
                            }
                        }}
                        label={t('enrollAxiadid$pivPinLabel')}
                        placeholder={t('enrollAxiadid$pivPinPlaceholder')}
                        autoFocus
                    />
                    {pinFormData.error && (
                        <div className="alert alert-danger mt-3">
                            {pinFormData.error}
                        </div>
                    )}
                    {supportLink}
                </div>
            ) : (
                <>
                    {qrData ? (
                        renderQRSection()
                    ) : (
                        <ProgressTemplate
                            isFake={false}
                            isDone={showNext}
                            stage={stage}
                        />
                    )}
                </>
            )}
        </CardEnrollLayout>
    );
};

export default EnrollAxiadid;
